// utils/answerAccuracy.js

/**
 * Cleans a string by removing spaces and punctuation, and converting it to lower case.
 * @param {string} str - The input string.
 * @returns {string} - A cleaned string with only significant (alphabetic/numeric) characters.
 */
function cleanString(str) {
    // First, remove all content within brackets (and the brackets themselves)
    let withoutBrackets = str;
    
    // Pattern to match content within any type of bracket
    const bracketPattern = /(\([^()]*\)|\[[^\[\]]*\]|\{[^{}]*\}|\<[^<>]*\>)/g;
    
    // Keep removing brackets until no more are found (handles nested brackets)
    let prevStr;
    do {
        prevStr = withoutBrackets;
        withoutBrackets = withoutBrackets.replace(bracketPattern, '');
    } while (withoutBrackets !== prevStr);
    
    // Then apply the original cleaning logic
    return withoutBrackets.replace(/[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/g, '').toLowerCase();
}
  
/**
 * Computes the standard Levenshtein edit distance between two strings.
 * @param {string} str1 - The first string (assumed to be already cleaned).
 * @param {string} str2 - The second string (assumed to be already cleaned).
 * @returns {number} - The edit distance between the two strings.
 */
function editDistance(str1, str2) {
const m = str1.length;
const n = str2.length;
// Create a 2D DP table of size (m+1) x (n+1)
const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

// Base cases: converting to/from an empty string.
for (let i = 0; i <= m; i++) {
    dp[i][0] = i;
}
for (let j = 0; j <= n; j++) {
    dp[0][j] = j;
}

// Compute dp for each sub-problem.
for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
    if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
    } else {
        dp[i][j] = 1 + Math.min(
        dp[i - 1][j],    // deletion
        dp[i][j - 1],    // insertion
        dp[i - 1][j - 1] // substitution
        );
    }
    }
}

return dp[m][n];
}
  
/**
 * Counts the number of significant characters in a string (those kept by cleanString).
 * @param {string} str - The input string.
 * @returns {number} - The count of significant characters.
 */
function countSignificantChars(str) {
    return cleanString(str).length;
}

/**
 * Checks if the user's answer is accurate enough compared with the correct answer.
 * The check uses an accuracy threshold of 95%. It cleans both answers,
 * computes the edit distance on the significant characters, and calculates an accuracy percentage.
 *
 * @param {string} userAnswer - The answer provided by the user.
 * @param {string} correctAnswer - The expected correct answer.
 * @returns {boolean} - true if accuracy is at least 95%, false otherwise.
 */
export function isAnswerAccurate(userAnswer, correctAnswer) {
const cleanedUser = cleanString(userAnswer);
const cleanedCorrect = cleanString(correctAnswer);
const userSignificantChars = cleanedUser.length;
const correctSignificantChars = cleanedCorrect.length;

// If the user answer has no significant characters but the correct answer does, reject immediately.
if (userSignificantChars === 0 && correctSignificantChars > 0) {
    return false;
}

// Compute edit distance on the cleaned (significant) strings.
const distance = editDistance(cleanedUser, cleanedCorrect);

// Calculate accuracy using the correct answer's significant character count.
// If there are no significant characters in the correct answer, consider the accuracy as:
// 100 if both are empty, 0 otherwise.
const accuracy = correctSignificantChars > 0
    ? (1 - (distance / correctSignificantChars)) * 100
    : (userSignificantChars === 0 ? 100 : 0);

    console.log('Accuracy ', accuracy)
return accuracy >= 90;
}

// ===========================
// Test Cases (for debugging)
// ===========================
//   if (require.main === module) {
//     /**
//      * Helper function to run and print a test case.
//      * @param {string} userAnswer - The answer provided by the user.
//      * @param {string} correctAnswer - The expected correct answer.
//      * @param {boolean} expected - The expected boolean result.
//      */
//     function test(userAnswer, correctAnswer, expected) {
//       const result = isAnswerAccurate(userAnswer, correctAnswer);
//       console.log(`isAnswerAccurate(${JSON.stringify(userAnswer)}, ${JSON.stringify(correctAnswer)}) -> ${result} (expected: ${expected})`);
//     }

//     // Provided example: Should now return false because the answer is missing "kommst"
//     test('Woher du', 'Woher kommst du?', false);

//     // Basic exact match, even if there is extra punctuation or spacing.
//     test('Woher kommst du?', 'Woher kommst du?', true);
//     test('Woher    kommst du!', 'Woher kommst du?', true);

//     // Casing differences should be ignored.
//     test('wOHer kommSt du?', 'Woher kommst du?', true);

//     // Both empty or only whitespace should be true.
//     test('', '', true);
//     test('   ', '', true);

//     // Non-empty correct answer but empty (or only spaces) user answer returns false.
//     test('   ', 'Was ist los?', false);

//     // A small typo: edit distance 1 out of 5 should not meet the 95% threshold.
//     test('Hello', 'Hillo', false);

//     // Completely missing words should return false.
//     test('Hello', 'Hello there', false);
//     test('Hello there', 'Hello there', true);

//     // Extra letters: if the edit distance is 1 but the correct answer is short, it might drop below threshold.
//     test('Helloo there', 'Hello there', false);

//     // Punctuation differences should be ignored.
//     test('Hello there', 'Hello, there!', true);

//     // Debug prints: show exact accuracy for a couple of examples.
//     function printAccuracy(userAnswer, correctAnswer) {
//       const cleanedUser = cleanString(userAnswer);
//       const cleanedCorrect = cleanString(correctAnswer);
//       const distance = editDistance(cleanedUser, cleanedCorrect);
//       const accuracy = cleanedCorrect.length > 0
//         ? (1 - (distance / cleanedCorrect.length)) * 100
//         : (cleanedUser.length === 0 ? 100 : 0);
//       console.log(`Accuracy for ${JSON.stringify(userAnswer)} vs ${JSON.stringify(correctAnswer)}: ${accuracy.toFixed(2)}% (distance: ${distance}, correct length: ${cleanedCorrect.length})`);
//     }

//     printAccuracy('Woher du', 'Woher kommst du?');
//     printAccuracy('Hello', 'Hillo');
//     printAccuracy('Helloo there', 'Hello there');
//   }
